import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { apiCall } from '../utils/ApiUtils';
import Modal from './common/Modal';
import './Login.css';

const Login = () => {
  const { login, error } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [localError, setLocalError] = useState('');

  // Forgot-password modal state
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotMsg, setForgotMsg] = useState('');
  const [forgotMsgColor, setForgotMsgColor] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLocalError('');

    if (!username.trim()) {
      setLocalError('Username is required');
      return;
    }
    if (!password) {
      setLocalError('Password is required');
      return;
    }

    setIsLoading(true);
    const result = await login(username, password, rememberMe);
    setIsLoading(false);

    if (!result.success) {
      setLocalError(result.error || 'Login failed. Please try again.');
    }
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    setForgotMsg('');
    if (!forgotEmail.trim()) {
      setForgotMsg('Email is required');
      setForgotMsgColor('var(--status-low-text)');
      return;
    }
    setForgotLoading(true);
    try {
      const data = await apiCall('/auth/forgot-password', {
        method: 'POST',
        body: JSON.stringify({ email: forgotEmail.trim() }),
      });
      setForgotMsg(data.message || 'If that email exists, a reset link has been sent.');
      setForgotMsgColor('var(--status-adequate-text)');
    } catch (err) {
      setForgotMsg(err.message || 'Request failed. Please try again.');
      setForgotMsgColor('var(--status-low-text)');
    } finally {
      setForgotLoading(false);
    }
  };

  const displayError = localError || error;

  return (
    <div className="login-container">
      <div className="login-box">
        <div className="login-header">
          <i className="fas fa-flask login-icon"></i>
          <h1>Lab Inventory Management</h1>
          <p>Please sign in to continue</p>
        </div>

        <form onSubmit={handleSubmit} className="login-form">
          {displayError && (
            <div className="login-error">
              <i className="fas fa-exclamation-circle"></i>
              <span>{displayError}</span>
            </div>
          )}

          <div className="form-group">
            <label htmlFor="username">
              <i className="fas fa-user"></i>
              Username or Email
            </label>
            <input
              type="text"
              id="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Enter your username or email"
              autoComplete="username"
              disabled={isLoading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">
              <i className="fas fa-lock"></i>
              Password
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                autoComplete="current-password"
                disabled={isLoading}
                // No minLength here: legacy passwords set under the old 6-char
                // minimum must still be submittable (backend login allows them).
                style={{ paddingRight: '40px' }}
              />
              <button
                type="button"
                onClick={() => setShowPassword(s => !s)}
                title={showPassword ? 'Hide password' : 'Show password'}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                style={{
                  position: 'absolute', right: '4px', top: '50%', transform: 'translateY(-50%)',
                  background: 'none', border: 'none', cursor: 'pointer', fontSize: '16px',
                  color: 'var(--text-secondary)', padding: '12px', minWidth: '44px', minHeight: '44px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >
                <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
              </button>
            </div>
          </div>

          <div className="form-group" style={{ flexDirection: 'row', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
            <input
              type="checkbox"
              id="rememberMe"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
              disabled={isLoading}
            />
            <label htmlFor="rememberMe" style={{ marginBottom: 0, cursor: 'pointer', fontWeight: 400 }}>
              Remember me (7 days)
            </label>
          </div>

          <button type="submit" className="login-button" disabled={isLoading}>
            {isLoading ? (
              <>
                <i className="fas fa-spinner fa-spin"></i>
                Signing in...
              </>
            ) : (
              <>
                <i className="fas fa-sign-in-alt"></i>
                Sign In
              </>
            )}
          </button>

          <div style={{ textAlign: 'center', marginTop: '4px' }}>
            <button
              type="button"
              onClick={() => { setForgotEmail(username); setForgotMsg(''); setShowForgot(true); }}
              style={{ color: 'var(--brand-primary)', textDecoration: 'none', background: 'none', border: 'none', cursor: 'pointer', minHeight: '44px', padding: 'var(--space-sm)', display: 'inline-flex', alignItems: 'center', fontSize: 'var(--text-sm)' }}
            >
              Forgot password?
            </button>
          </div>
        </form>

        <div className="login-footer">
          <p className="security-note">Contact your administrator for account credentials.</p>
        </div>
      </div>

      {/* Forgot password modal */}
      {showForgot && (
        <Modal onClose={() => setShowForgot(false)} titleId="forgot-title" dialogClassName="modal-dialog--sm">
          <h2 id="forgot-title" className="sr-only">Reset Password</h2>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
            <h3 style={{ margin: 0, fontSize: '1.15rem' }}>
              <i className="fas fa-key" style={{ marginRight: '0.5rem', color: 'var(--brand-primary)' }} aria-hidden="true"></i>
              Reset Password
            </h3>
            <button type="button" onClick={() => setShowForgot(false)} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: 'var(--text-secondary)', lineHeight: 1, minWidth: '44px', minHeight: '44px', padding: 0 }} aria-label="Close">&times;</button>
          </div>
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', marginBottom: '1rem' }}>
            Enter your email to receive a reset link. The link expires in 1 hour.
          </p>
          <form onSubmit={handleForgot}>
            <div className="form-group">
              <label htmlFor="forgotEmail" style={{ fontSize: '0.85rem' }}>Email</label>
              <input
                type="email"
                id="forgotEmail"
                value={forgotEmail}
                onChange={(e) => setForgotEmail(e.target.value)}
                placeholder="you@example.com"
                autoComplete="email"
                disabled={forgotLoading}
                required
              />
            </div>
            {forgotMsg && (
              <div style={{ background: forgotMsgColor === 'var(--status-adequate-text)' ? 'var(--status-adequate-bg)' : 'var(--status-low-bg)', border: `1px solid ${forgotMsgColor === 'var(--status-adequate-text)' ? 'var(--status-adequate-border)' : 'var(--status-low-border)'}`, borderRadius: '8px', padding: '10px', color: forgotMsgColor, fontSize: '0.85rem', marginBottom: '0.75rem' }}>
                {forgotMsg}
              </div>
            )}
            <button type="submit" className="login-button" disabled={forgotLoading}>
              {forgotLoading ? (
                <><i className="fas fa-spinner fa-spin"></i> Sending...</>
              ) : (
                <><i className="fas fa-paper-plane"></i> Send Reset Link</>
              )}
            </button>
          </form>
        </Modal>
      )}
    </div>
  );
};

export default Login;